import React from 'react'
import styled from 'styled-components'

const Container = styled.div`
  min-height: 100vh;
  padding: 6rem var(--container-padding-desktop) 4rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );

  @media (max-width: 1024px) {
    padding: 6rem var(--container-padding-tablet) 4rem;
  }

  @media (max-width: 768px) {
    padding: 5rem var(--container-padding-mobile) 3rem;
  }
`

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 2.5rem);
  color: var(--color-primary);
  margin-bottom: 2rem;
`

const Section = styled.section`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--color-text);
  margin-bottom: 1rem;
`

const Text = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const List = styled.ul`
  list-style-type: disc;
  color: var(--color-text-secondary);
  margin: 1rem 0 1rem 1.5rem;
  line-height: 1.8;

  li {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Table = styled.table`
  width: 100%;
  margin: 1.5rem 0;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  th {
    background: rgba(64, 224, 208, 0.1);
    color: var(--color-primary);
    font-weight: 600;
  }

  td {
    color: var(--color-text-secondary);
  }

  tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`

const LastUpdated = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin-bottom: 2rem;
`

export default function Cookies() {
  return (
    <Container>
      <Content>
        <Title>Cookie Policy</Title>
        <LastUpdated>Last Updated: February 15, 2024</LastUpdated>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <Text>
            This Cookie Policy explains how OmniBrain uses cookies and similar technologies
            to recognize you when you visit our platform. It explains what these technologies
            are and why we use them, as well as your rights to control our use of them.
          </Text>
        </Section>

        <Section>
          <SectionTitle>2. What Are Cookies</SectionTitle>
          <Text>
            Cookies are small data files that are placed on your computer or mobile device
            when you visit a website. They are widely used by website owners to make their
            websites work, or work more efficiently, as well as to provide reporting information.
          </Text>
        </Section>

        <Section>
          <SectionTitle>3. Types of Cookies We Use</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Essential Cookies</td>
                <td>Required for basic platform functionality, including wallet connection and authentication</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>Performance Cookies</td>
                <td>Help us understand how visitors interact with our platform</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>Functionality Cookies</td>
                <td>Remember your preferences and personalize your experience</td>
                <td>6 months</td>
              </tr>
              <tr>
                <td>Analytics Cookies</td>
                <td>Collect data to help us improve our platform and services</td>
                <td>2 years</td>
              </tr>
            </tbody>
          </Table>
        </Section>

        <Section>
          <SectionTitle>4. How We Use Cookies</SectionTitle>
          <Text>
            We use cookies for the following purposes:
          </Text>
          <List>
            <li>To enable certain functions of the platform</li>
            <li>To provide analytics and understand usage patterns</li>
            <li>To store your preferences</li>
            <li>To enable secure wallet connections</li>
            <li>To optimize platform performance</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. Your Cookie Choices</SectionTitle>
          <Text>
            You have the right to decide whether to accept or reject cookies. You can set
            or amend your web browser controls to accept or refuse cookies. If you choose
            to reject cookies, you may still use our platform, though your access to some
            functionality and areas may be restricted.
          </Text>
        </Section>

        <Section>
          <SectionTitle>6. Essential Cookies</SectionTitle>
          <Text>
            Some cookies are essential for the operation of our platform. In particular,
            cookies related to wallet connection and authentication are required for the
            secure operation of our services. These cookies cannot be disabled through
            the platform settings.
          </Text>
        </Section>

        <Section>
          <SectionTitle>7. Third-Party Cookies</SectionTitle>
          <Text>
            In addition to our own cookies, we may also use various third-party cookies
            to report usage statistics, deliver advertisements, and so on. These cookies
            may track your browsing habits across different websites and online services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>8. Updates to This Policy</SectionTitle>
          <Text>
            We may update this Cookie Policy from time to time to reflect changes to the
            cookies we use or for other operational, legal, or regulatory reasons. Please
            revisit this policy regularly to stay informed about our use of cookies.
          </Text>
        </Section>

        <Section>
          <SectionTitle>9. Contact Us</SectionTitle>
          <Text>
            If you have any questions about our use of cookies, please contact us at
            privacy@omnibrain.ai
          </Text>
        </Section>
      </Content>
    </Container>
  )
}
